import React, { useState, useEffect } from "react";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PieChartIcon from "@mui/icons-material/PieChart";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import Diversity2Icon from "@mui/icons-material/Diversity2";
import MessageIcon from "@mui/icons-material/Message";
import { SupportAgent } from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./Dashboard.module.css";

export default function Sidebar({ onLogoff }) {
  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  // Responsive sidebar state
  const [collapsed, setCollapsed] = useState(false);

  // Detect screen size and set collapsed by default for medium and smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Use a fallback role if not set
  const role = localStorage.getItem("role") || "SuperAdmin";

  // Define menu items by role according to access requirements
  const menuItems = {
    SuperAdmin: [
      { to: "/superadmin", icon: <DashboardIcon />, text: "Dashboard" },
      {
        to: "/superadmin/users",
        icon: <AdminPanelSettingsIcon />,
        text: "Users",
      },
      {
        to: "/superadmin/clients",
        icon: <AddBusinessIcon />,
        text: "Clients",
      },
      {
        to: "/superadmin/operators",
        icon: <SupportAgent />,
        text: "Operators",
      },
      { to: "/superadmin/groups", icon: <Diversity2Icon />, text: "Groups" },
      { to: "/superadmin/messages", icon: <MessageIcon />, text: "Messages" },
      { to: "/superadmin/reports", icon: <PieChartIcon />, text: "Reports" },
      {
        to: "/superadmin/consumers",
        icon: <PeopleAltIcon />,
        text: "Consumers",
      },
    ],
    ClientAdmin: [
      // ClientAdmin: NO access to Users and Clients, but has access to Operators and other features
      { to: "/clientadmin", icon: <DashboardIcon />, text: "Dashboard" },
      {
        to: "/clientadmin/operators",
        icon: <SupportAgent />,
        text: "Operators",
      },
      { to: "/clientadmin/messages", icon: <MessageIcon />, text: "Messages" },
      {
        to: "/clientadmin/consumers",
        icon: <PieChartIcon />,
        text: "Consumers",
      },
    ],
    Operator: [{ to: "/operator", icon: <MessageIcon />, text: "Messages" }],
    Consumer: [
      { to: "/consumer", icon: <DashboardIcon />, text: "Dashboard" },
      { to: "/consumer/messages", icon: <MessageIcon />, text: "My Messages" },
      { to: "/consumer/profile", icon: <PeopleAltIcon />, text: "Profile" },
    ],
  };

  return (
    <div
      className={
        collapsed
          ? `${styles.sidebar} ${styles.sidebarCollapsed}`
          : styles.sidebar
      }
    >
      {/* User Profile Section with collapse/expand button for medium and lower screens */}
      <div className={styles.userProfile}>
        <div className={styles.avatar}>
          <div className={styles.avatarCircle}>
            <svg
              width="46"
              height="46"
              viewBox="0 0 24 24"
              style={{ display: "block", margin: "auto" }}
            >
              {/* Standard professional user icon with yellow head and blue body - bigger size */}
              <circle cx="12" cy="8" r="4" fill="#FFD600" />
              <path
                d="M12 14c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                fill="#1976D2"
              />
            </svg>
          </div>
        </div>
        <div className={styles.userInfo}>
          <p className={styles.welcomeText}>
            Welcome <br /> {userData.name || userData.email || "User"}
          </p>
        </div>
        {/* Collapse/Expand Button - only visible on medium and lower screens */}
        <div className={styles.collapseBtnWrapper}>
          {collapsed ? (
            <button
              className={styles.collapseBtn}
              aria-label="Expand sidebar"
              onClick={() => setCollapsed(false)}
            >
              <MenuIcon />
            </button>
          ) : (
            <button
              className={styles.collapseBtn}
              aria-label="Collapse sidebar"
              onClick={() => setCollapsed(true)}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Navigation Menu - hidden when collapsed on medium/lower screens */}
      {!collapsed && (
        <nav className={styles.navigation}>
          <ul className={styles.menuList}>
            {menuItems[role]?.map((item, index) => {
              return (
                <li key={index}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      isActive
                        ? `${styles.navItem} ${styles.active}`
                        : styles.navItem
                    }
                    end={item.to === `/${role.toLowerCase()}`}
                  >
                    <span className={styles.navIcon}>{item.icon}</span>
                    <span className={styles.navText}>{item.text}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      )}

      {/* Logoff Button - hidden when collapsed on medium/lower screens */}
      {!collapsed && (
        <div className={styles.logoffSection}>
          <button onClick={onLogoff} className={styles.logoffBtn}>
            <span className={styles.navIcon}>
              <ExitToAppIcon />
            </span>
            <span className={styles.navText}>Logoff</span>
          </button>
        </div>
      )}
    </div>
  );
}
